"use client";

import { Eye, EyeOff } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import { Button } from "../../../elements/button/button";
import { TextD2C } from "../../../elements/text-d2c/text";
import { mainNavigationClasses } from "../main-navigation.constants";

export interface NavigationLoginCredentials {
  email: string;
  password: string;
}

export interface NavigationLoginProps {
  /**
   * Signs the customer in and resolves with the message to show when that
   * failed. On success the app decides what follows, e.g. a refresh.
   */
  onSubmit: (
    credentials: NavigationLoginCredentials
  ) => Promise<{ error?: string }>;
  forgotPasswordHref: string;
  registerHref: string;
  microcopies: {
    title: string;
    existingCustomer: string;
    email: string;
    password: string;
    showPassword: string;
    hidePassword: string;
    forgotPassword: string;
    submit: string;
    newCustomer: string;
    register: string;
    /** Shown when signing in fails without a message of its own. */
    genericError: string;
  };
}

// iOS zooms into fields below 16px, so the design's 12px only applies on desktop.
const inputClassName =
  "h-10.5 w-full rounded-sm border border-alabastergrey bg-white px-3 font-brand text-[16px] text-black outline-none focus:border-black lg:text-[12px]";
const submitClassName = "h-10.5 w-full";

/**
 * Login panel of the header: the form for existing customers next to the way
 * to register. Fields rely on the browser's validation for required values
 * and the email format.
 */
export function NavigationLogin(props: Readonly<NavigationLoginProps>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [showsPassword, setShowsPassword] = useState(false);
  const idPrefix = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setIsSubmitting(true);
    setError(undefined);

    try {
      const result = await props.onSubmit({
        email: String(formData.get("email") ?? "").trim(),
        password: String(formData.get("password") ?? ""),
      });
      setError(result.error);
    } catch {
      setError(props.microcopies.genericError);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <TextD2C style="no-mb" tagName="h2" variant="h5">
        {props.microcopies.title}
      </TextD2C>

      <div className="grid gap-4 md:grid-cols-2">
        <form
          aria-labelledby={`${idPrefix}-existing`}
          className="flex flex-col gap-8 bg-whitesmoke p-6"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-4">
            <TextD2C
              id={`${idPrefix}-existing`}
              style="no-mb"
              tagName="h3"
              variant="p"
            >
              {props.microcopies.existingCustomer}
            </TextD2C>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-4">
                <LoginField
                  id={`${idPrefix}-email`}
                  label={props.microcopies.email}
                >
                  <input
                    autoComplete="email"
                    className={inputClassName}
                    id={`${idPrefix}-email`}
                    name="email"
                    required={true}
                    type="email"
                  />
                </LoginField>
                <LoginField
                  id={`${idPrefix}-password`}
                  label={props.microcopies.password}
                >
                  <div className="relative">
                    <input
                      autoComplete="current-password"
                      className={`${inputClassName} pr-11`}
                      id={`${idPrefix}-password`}
                      name="password"
                      required={true}
                      type={showsPassword ? "text" : "password"}
                    />
                    <Button
                      aria-label={
                        showsPassword
                          ? props.microcopies.hidePassword
                          : props.microcopies.showPassword
                      }
                      className="-translate-y-1/2 absolute top-1/2 right-1 rounded-full"
                      mode="D2C"
                      onClick={() => setShowsPassword(!showsPassword)}
                      size="icon-sm"
                      type="button"
                      variant="ghost"
                    >
                      {showsPassword ? (
                        <Eye aria-hidden="true" />
                      ) : (
                        <EyeOff aria-hidden="true" />
                      )}
                    </Button>
                  </div>
                </LoginField>
              </div>
              <Button
                className={`${mainNavigationClasses.focusRing} self-end font-brand text-[10px] leading-[13px] hover:underline`}
                href={props.forgotPasswordHref}
                mode="D2C"
                unstyled={true}
              >
                {props.microcopies.forgotPassword}
              </Button>
            </div>
          </div>

          {error ? (
            <p
              className="font-brand text-[12px] text-destructive leading-4"
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <Button
            className={`mt-auto ${submitClassName}`}
            loading={isSubmitting}
            mode="D2C"
            size="sm"
            type="submit"
          >
            {props.microcopies.submit}
          </Button>
        </form>

        <section
          aria-labelledby={`${idPrefix}-new`}
          className="flex flex-col gap-8 bg-whitesmoke p-6"
        >
          <TextD2C
            id={`${idPrefix}-new`}
            style="no-mb"
            tagName="h3"
            variant="p"
          >
            {props.microcopies.newCustomer}
          </TextD2C>
          <Button
            className={`mt-auto bg-transparent ${submitClassName}`}
            href={props.registerHref}
            mode="D2C"
            size="sm"
            variant="outline"
          >
            {props.microcopies.register}
          </Button>
        </section>
      </div>
    </div>
  );
}

function LoginField(
  props: Readonly<{ id: string; label: string; children: ReactNode }>
) {
  return (
    <div className="flex flex-col gap-2">
      <label
        className="font-brand text-[10px] text-black leading-[13px]"
        htmlFor={props.id}
      >
        {props.label}
      </label>
      {props.children}
    </div>
  );
}
